/**
 * The small badge / pill, in the three shapes the surface uses:
 *   `badge` a static milestone tag on an inert card (22, radius 6)
 *   `meta`  a rail-header context chip (28, radius 8, medium 12)
 *   `pill`  a pressable toggle — the design lab's chrome (30, full radius)
 */
import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';

import {RADIUS, SIZE, SPACE} from '../theme/tokens';
import {useV2Theme} from '../theme/useV2Theme';
import Txt, {TxtVariant} from './Txt';

export type ChipVariant = 'badge' | 'meta' | 'pill';

/** the header chip's radius — between `badge` and `panel`, per design L4 */
const META_RADIUS = 8;

const TEXT: {[K in ChipVariant]: TxtVariant} = {
  badge: 'badge',
  meta: 'chip',
  pill: 'labelSm',
};

export type ChipProps = {
  label: string;
  variant?: ChipVariant;
  active?: boolean;
  onPress?: () => void;
};

function Chip({label, variant = 'badge', active, onPress}: ChipProps) {
  const tokens = useV2Theme(s => s.tokens);

  const box = [
    styles[variant],
    {
      backgroundColor:
        variant === 'pill'
          ? active
            ? tokens.pri
            : tokens.well
          : 'transparent',
      boxShadow: `inset 0 0 0 1px ${tokens.line2}`,
    } as any,
  ];
  const tone =
    variant === 'badge'
      ? 'muted'
      : variant === 'pill' && active
      ? 'onPri'
      : 'ink2';

  const text = (
    <Txt variant={TEXT[variant]} tone={tone}>
      {label}
    </Txt>
  );

  if (!onPress) {
    return <View style={box}>{text}</View>;
  }
  return (
    <Pressable onPress={onPress} style={box}>
      {text}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  badge: {
    height: SIZE.badge,
    paddingHorizontal: SPACE.sm,
    borderRadius: RADIUS.badge,
    justifyContent: 'center',
  },
  meta: {
    height: SIZE.chip,
    paddingHorizontal: 10,
    borderRadius: META_RADIUS,
    justifyContent: 'center',
  },
  pill: {
    height: SIZE.chipLg,
    paddingHorizontal: SPACE.md,
    borderRadius: RADIUS.pill,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default React.memo(Chip);
